/**
 * @fileoverview Teaching materials — a class's curriculum and its lesson plans.
 *
 * The same three-step upload as lesson attachments (mint a presigned PUT,
 * upload, confirm), against `/org/classes/{id}/materials` for the
 * organization and `/classes/{id}/materials` for the class's instructors. One
 * helper serves both: the caller passes its API client and base path.
 *
 * Two scopes. A `class_curriculum` is kept as revisions, newest first, so what
 * was taught in October can be found a year later; the class keeps twenty and
 * the twenty-first is refused rather than the oldest pruned. A `meeting_plan`
 * is one file on one session, replaced rather than versioned.
 */

import axios from "axios";
import { formatBytes, resolveAttachmentType } from "@/lib/attachments";

export const CURRICULUM = "class_curriculum";
export const MEETING_PLAN = "meeting_plan";

/** Mirrors the backend's MAX_CURRICULUM_REVISIONS. */
export const MAX_CURRICULUM_REVISIONS = 20;
/** Mirrors the backend's MAX_MATERIAL_BYTES (the attachment cap). */
export const MAX_MATERIAL_BYTES = 10 * 1024 * 1024;

const DOCX = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const PPTX = "application/vnd.openxmlformats-officedocument.presentationml.presentation";

// The attachment allowlist plus the two Office formats a curriculum usually
// is. Must stay in sync with the backend's MATERIAL_TYPES, the real allowlist.
const OFFICE_TYPES = { [DOCX]: "docx", [PPTX]: "pptx" };
const OFFICE_EXTENSIONS = { docx: DOCX, pptx: PPTX };

export const MATERIAL_ACCEPT = [
  "application/pdf", "image/jpeg", "image/png", "image/webp", "image/heic", "audio/mpeg", "audio/mp4", "audio/wav",
  DOCX, PPTX,
  ".pdf", ".jpg", ".jpeg", ".png", ".webp", ".heic", ".mp3", ".m4a", ".wav", ".docx", ".pptx",
].join(",");

export const MATERIAL_HINT = "PDF, Word, PowerPoint, image or audio · up to 10 MB";

/** Who uploaded a revision, as the other party reads it. */
export const ROLE_LABELS = { org_admin: "Organization", instructor: "Instructor" };

/**
 * The MIME type a picked material will be declared as, or "" when it is not
 * allowed. The attachment resolver first (it knows the iOS empty-type quirk),
 * then the Office formats.
 */
export function resolveMaterialType(file) {
  const attachment = resolveAttachmentType(file);
  if (attachment) return attachment;
  const type = (file?.type || "").split(";")[0].trim().toLowerCase();
  if (OFFICE_TYPES[type]) return type;
  if (type) return "";
  const ext = (file?.name || "").split(".").pop()?.toLowerCase();
  return OFFICE_EXTENSIONS[ext] || "";
}

/** The reason a file cannot be uploaded, or null. */
export function validateMaterial(file) {
  if (!resolveMaterialType(file)) return "That file type can't be uploaded. Use a PDF, Word, PowerPoint, image or audio file.";
  if (file.size > MAX_MATERIAL_BYTES) return `Files must be under ${formatBytes(MAX_MATERIAL_BYTES)}.`;
  return null;
}

/**
 * Mint, upload, confirm. Resolves to the confirmed material row.
 *
 * @param {object} client the API client for this portal (`api` or `orgApi`)
 * @param {string} basePath `/classes/{id}` or `/org/classes/{id}`
 * @param {File} file
 * @param {{scope: string, occurrence_id?: string, note?: string, onProgress?: Function}} options
 */
export async function uploadMaterial(client, basePath, file, { scope, occurrence_id, note, onProgress } = {}) {
  const contentType = resolveMaterialType(file);
  const { data: mint } = await client.post(`${basePath}/materials`, {
    scope,
    filename: file.name,
    content_type: contentType,
    size_bytes: file.size,
    ...(occurrence_id ? { occurrence_id } : {}),
  });
  // Bare axios: the presigned S3 URL must not get the API client's auth
  // headers, and Content-Type must match what the backend signed.
  await axios.put(mint.upload_url, file, {
    headers: { "Content-Type": contentType },
    onUploadProgress: (e) => {
      if (e.total && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
  const { data } = await client.post(`${basePath}/materials/${mint.material_id}/confirm`, {
    scope,
    filename: file.name,
    content_type: contentType,
    ...(occurrence_id ? { occurrence_id } : {}),
    ...(note ? { note } : {}),
  });
  return data.material;
}

/** Only the curriculum revisions, newest first, as the server orders them. */
export function curriculumRevisions(materials) {
  return (materials || []).filter((m) => m.scope === CURRICULUM);
}

/** "Sep 12, 2026" from an ISO timestamp. */
export function uploadedOn(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** "Robin Vance (Instructor)" — who put a file here, and in which capacity. */
export function uploaderLabel(material) {
  const role = ROLE_LABELS[material?.uploaded_by_role] || "";
  const name = material?.uploaded_by_name;
  if (name && role) return `${name} (${role})`;
  return name || role || "Unknown";
}
